import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { CodedProperty, getFileNameWithExtension } from "sprint-common-web";

import {
    GetFileRecordDetailsResponse,
    VideoFileRecordDetailsResponse,
    AudioFileRecordDetailsResponse,
    SubtitleFileRecordDetailsResponse,
    CleanVideoSubtitleId,
    EmbeddedSubtitle
} from "../../../../../common/api/master-api/file-records/details/GetFileRecordDetailsResponse";
import { useCachedFileRecordsApi } from "../../../../../common/api/master-api/file-records/useCachedFileRecordsApi";
import { useNotesActions } from "../../../../../common/components/notes/hooks/useNotesActions";
import {
    ContentDetails,
    isAncillaryContentDetails,
    isFullTvSeriesContentDetails,
    isMovieContentDetails
} from "../../../../../common/types/ContentDetails";
import { ContentReferenceType } from "../../../../../common/types/ContentReferenceType";
import { RecordType } from "../../../../../common/types/RecordType";
import { NoteItem } from "../../../../../orders/common/components/notes/NoteItem";
import { isVideoWithSubtitle } from "../../../../common/file-record-details/FileRecordDetails";
import { CreateFileRecordFormInputs, defaultFormInputs } from "../CreateFileRecordFormInputs";
import { useCreateFileRecordState } from "./state/useCreateFileRecordState";

export const useLoadFileRecordForm = () => {
    const location = useLocation();
    const urlParams = new URLSearchParams(location.search);
    const sourceFileRecordId = urlParams.get("sourceFileRecordId");

    const { getCachedFileRecordDetails } = useCachedFileRecordsApi();

    const { getNotes } = useNotesActions();

    const {
        sourceFileRecord,
        setSourceFileRecord,
        setSelectedRecordType,
        setSelectedStorageType,

        setIsLoading,
        form
    } = useCreateFileRecordState();

    useEffect(() => {
        if (sourceFileRecordId) {
            //TODO: refactor to use react query
            setIsLoading(true);

            getCachedFileRecordDetails({
                fileRecordId: sourceFileRecordId
            })
                .then(setSourceFileRecord)
                .finally(() => setIsLoading(false));
        }
    }, [sourceFileRecordId]);

    useEffect(() => {
        if (!sourceFileRecord) {
            return;
        }

        setIsLoading(true);

        setStatesAndResetForm(sourceFileRecord).finally(() => setIsLoading(false));
    }, [sourceFileRecord]);

    const setStatesAndResetForm = async (fileRecord: GetFileRecordDetailsResponse) => {
        await setFormStates(fileRecord);
        const notes = await getNotes([fileRecord.id]);

        const inputsToReset = {
            ...getBaseInputsToReset(fileRecord, notes),
            ...getTechnicalAndLanguageInputs(fileRecord),
            ...getContentInputs(fileRecord.content)
        };

        form.reset(inputsToReset);
    };

    const setFormStates = async (fileRecord: GetFileRecordDetailsResponse) => {
        setSelectedRecordType(Number(fileRecord.recordType));
        setSelectedStorageType(Number(fileRecord.storageType));
    };

    return {
        form
    };
};

const getBaseInputsToReset = (fileRecord: GetFileRecordDetailsResponse, notes: NoteItem[]) => {
    return {
        recordType: String(fileRecord.recordType),
        fileName: getFileNameWithExtension(String(fileRecord.delivery.file.path)),
        originator: String(fileRecord.delivery.originator.id),
        arrivalDate: new Date(fileRecord.delivery.arrivalDate),
        reference: fileRecord.delivery.reference ? String(fileRecord.delivery.reference) : defaultFormInputs.reference,
        storageType: fileRecord.storageType ? String(fileRecord.storageType) : defaultFormInputs.storageType,
        path: fileRecord.delivery.path ? String(fileRecord.delivery.path) : defaultFormInputs.path,
        notes: [...notes.map((note) => ({ value: note.content })).reverse(), { value: "" }]
    } as CreateFileRecordFormInputs;
};

const getTechnicalAndLanguageInputs = (fileRecord: GetFileRecordDetailsResponse) => {
    switch (fileRecord.recordType) {
        case RecordType.Video: {
            return getVideoInputs(fileRecord);
        }
        case RecordType.Audio: {
            return getAudioInputs(fileRecord);
        }
        case RecordType.Subtitle: {
            return getSubtitleInputs(fileRecord);
        }
    }
};

const getVideoInputs = (fileRecord: VideoFileRecordDetailsResponse) => {
    return {
        videoSoundtracks: getSoundtracks(fileRecord.soundtracks),
        videoSubtitle: getVideoSubtitle(fileRecord.subtitle),
        videoAspectRatio: fileRecord.metadata?.aspectRatio?.id
            ? String(fileRecord.metadata.aspectRatio.id)
            : defaultFormInputs.videoAspectRatio,
        videoAspectType: fileRecord.metadata?.aspectType?.id
            ? String(fileRecord.metadata?.aspectType?.id)
            : defaultFormInputs.videoAspectType,
        videoEncoding: fileRecord.metadata?.encoding
            ? Boolean(fileRecord.metadata?.encoding)
            : defaultFormInputs.videoEncoding,
        videoFramerate: fileRecord.metadata?.framerate
            ? String(fileRecord.metadata?.framerate)
            : defaultFormInputs.videoFramerate,
        videoBitrate: fileRecord.metadata?.videoBitRate
            ? String(fileRecord.metadata?.videoBitRate)
            : defaultFormInputs.videoBitrate,
        videoDuration: fileRecord.metadata?.duration
            ? String(fileRecord.metadata?.duration)
            : defaultFormInputs.videoDuration,
        videoResolution: fileRecord.metadata?.resolution
            ? String(fileRecord.metadata?.resolution)
            : defaultFormInputs.videoResolution,
        videoStandard: fileRecord.metadata?.standard?.id
            ? String(fileRecord.metadata?.standard?.id)
            : defaultFormInputs.videoStandard
    };
};
const getAudioInputs = (fileRecord: AudioFileRecordDetailsResponse) => {
    return {
        audioSoundtrack: fileRecord.soundtrack ? fileRecord.soundtrack.id : defaultFormInputs.audioSoundtrack,
        audioFormat: fileRecord.metadata?.audioFormat
            ? String(fileRecord.metadata?.audioFormat)
            : defaultFormInputs.audioFormat,
        audioSampleRate: fileRecord.metadata?.sampleRate
            ? String(fileRecord.metadata?.sampleRate)
            : defaultFormInputs.audioSampleRate,
        audioBitrate: fileRecord.metadata?.audioBitRate
            ? String(fileRecord.metadata?.audioBitRate)
            : defaultFormInputs.audioBitrate,
        audioChannels: fileRecord.metadata?.channelsCount
            ? String(fileRecord.metadata?.channelsCount)
            : defaultFormInputs.audioChannels,
        audioResolution: fileRecord.metadata?.resolution
            ? String(fileRecord.metadata?.resolution)
            : defaultFormInputs.audioResolution,
        audioDuration: fileRecord.metadata?.duration
            ? String(fileRecord.metadata?.duration)
            : defaultFormInputs.audioDuration
    };
};

const getSubtitleInputs = (fileRecord: SubtitleFileRecordDetailsResponse) => {
    return {
        subtitleSubtitles: fileRecord.subtitle?.id
            ? String(fileRecord.subtitle?.id)
            : defaultFormInputs.subtitleSubtitles
    };
};

const getContentInputs = (content?: ContentDetails) => {
    if (!content) {
        return undefined;
    }

    if (isMovieContentDetails(content)) {
        return {
            contentType: ContentReferenceType.Movie,
            contentId: content.movie.id,
            versionIds: content.versions.map((version) => version.id)
        };
    }

    if (isFullTvSeriesContentDetails(content)) {
        return {
            contentType: ContentReferenceType.TvSeries,
            contentId: content.tvSeries.id,
            episodeId: content.episode?.id,
            seasonId: content.season?.id,
            versionIds: content.versions.map((version) => version.id)
        };
    }

    if (isAncillaryContentDetails(content)) {
        return {
            contentType: ContentReferenceType.Ancillary,
            contentId: content.ancillary.id,
            airlineId: content.airline.id,
            versionIds: content.versionSummary.ids
        };
    }

    return undefined;
};

const getVideoSubtitle = (subtitle?: EmbeddedSubtitle): string | undefined => {
    if (!subtitle) {
        return undefined;
    }

    if (isVideoWithSubtitle(subtitle)) {
        return subtitle.language.id;
    }

    return CleanVideoSubtitleId;
};

const getSoundtracks = (soundtracks: CodedProperty[]): string[] => {
    return soundtracks ? soundtracks.map((soundtrack) => String(soundtrack.id)) : defaultFormInputs.videoSoundtracks;
};
